import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, Tag, ChevronLeft, ChevronDown, Star, BadgeCheck } from "lucide-react";
import { PaymentForm } from "./PaymentForm";
import { useCreateBookingPayment, useProfile } from "../../lib/api";
import { useGetPaymentOptions } from "../../lib/api/usePayment";
import { UserAPI } from "@shared/types";
import { CANCELLATION_REFUND_WINDOW_HOURS } from "@shared/constants/constants";
import {
  buildPaywallTestimonials,
  pickPaywallSegment,
} from "../../data/paywallTestimonials";
import { useToast } from "../ux/Toast";
import { usePurchase, useIsNative, usePlatform } from "../../lib/platform";
import type { NativeStripeOptions } from "../../lib/platform/purchase";
import { useGuaranteeDays, useNativeStripeEnabled } from "../../lib/features";
import { getReferrerCode, clearReferrerCode } from "../../lib/referral";
import { formatPrice } from "../../utils/priceUtils";
import { formatDate, DateFormats } from "../../utils/dateUtils";
import { useT } from "../../lib/i18n";
import tracking from "../../lib/tracking";

// Paywall social-proof figures. Locale-formatted at render so de/fr see "4,8" and "35.000".
const MEMBER_RATING = 4.8;
const MEMBER_COUNT = 35000;

interface PaymentModalProps {
  bookingId?: string;
  paymentOptions: UserAPI.PaymentOption[];
  /**
   * Open with this plan already chosen. For surfaces that have their own plan
   * list and hand over on a press — the onboarding offer screen — where
   * arriving on a different plan than the one just pressed reads as the sheet
   * ignoring the choice.
   */
  preselectedOptionId?: string;
  startDate?: Date | null;
  subscriptionOnly?: boolean;
  /** A coupon already applied to `paymentOptions` upstream. Shown as applied, and it wins over a stored referral. */
  couponCode?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function PaymentModalV2({
  bookingId,
  paymentOptions: initialPaymentOptions,
  preselectedOptionId,
  startDate,
  subscriptionOnly,
  couponCode: upstreamCoupon,
  onSuccess: onSuccessPayment,
  onCancel,
}: PaymentModalProps) {
  if (subscriptionOnly || !bookingId) {
    initialPaymentOptions = initialPaymentOptions.filter((option) => option.type === "subscription");
  }
  const preselected =
    initialPaymentOptions.find((option) => option.id === preselectedOptionId) ??
    null;
  const [selectedOption, setSelectedOption] =
    useState<UserAPI.PaymentOption | null>(preselected);
  const [paymentData, setPaymentData] =
    useState<UserAPI.CreateBookingPaymentData | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(
    upstreamCoupon ?? null
  );
  const [paymentOptions, setPaymentOptions] = useState<UserAPI.PaymentOption[]>(
    initialPaymentOptions
  );
  const [paymentType, setPaymentType] = useState<"subscription" | "one_time">(
    preselected?.type === "one_time" ? "one_time" : "subscription"
  );
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [isNativePurchasing, setIsNativePurchasing] = useState(false);
  const createPayment = useCreateBookingPayment();
  const getPaymentOptions = useGetPaymentOptions();
  const toast = useToast();
  const purchase = usePurchase();
  const isNative = useIsNative();
  const { platform } = usePlatform();
  const isAppleNative = isNative && platform === 'ios';
  const useNativeStripe = useNativeStripeEnabled();
  const guaranteeDays = useGuaranteeDays();
  const { t, language } = useT();

  // V2 always uses the Membership / Single Outing labels (the v1/v2 split itself
  // is the A/B test, so the relabel travels with the V2 surface).
  const toggleVariant = "v2";

  // Coarse paywall segmentation — M20-39 / M40+ / Sapphic. Falls back to Sapphic for
  // any user we can't bucket as a man (women, nonbinary, prefer-not-say, missing data).
  const { data: profileData } = useProfile();
  const profile = profileData?.data?.profile;
  const paywallSegment = pickPaywallSegment({
    genderIdentity: profile?.genderIdentity,
    pronouns: profile?.pronouns,
    birthday: profile?.birthday,
  });
  // Quote text is translated; names/ages/cities come from code. See paywallTestimonials.ts.
  const testimonialTrio = useMemo(() => {
    const quotes = t(`payment.testimonials.${paywallSegment}`, {
      returnObjects: true,
    } as Record<string, unknown>) as unknown as string[];
    return buildPaywallTestimonials(
      paywallSegment,
      language,
      Array.isArray(quotes) ? quotes : []
    );
  }, [paywallSegment, language, t]);

  // Avatar background palette — picked deterministically from the name's first char
  // so the same testimonial always renders the same color.
  const avatarPalette = [
    "bg-orange-500",
    "bg-pink-500",
    "bg-sky-500",
    "bg-emerald-500",
    "bg-purple-500",
  ];
  const avatarBgFor = (name: string) =>
    avatarPalette[(name.charCodeAt(0) || 0) % avatarPalette.length];

  // Check if both payment types are available
  const hasSubscriptions = paymentOptions.some(
    (option) => option.type !== "one_time" && option.interval_months
  );
  const hasOneTime = paymentOptions.some(
    (option) => option.type === "one_time"
  );
  const showToggle = hasSubscriptions && hasOneTime;

  // Auto-set payment type if only one type is available
  useEffect(() => {
    if (!showToggle) {
      if (hasOneTime && !hasSubscriptions) {
        setPaymentType("one_time");
      } else if (hasSubscriptions && !hasOneTime) {
        setPaymentType("subscription");
      }
    }
  }, [paymentOptions, hasOneTime, hasSubscriptions, showToggle]);

  // Filter payment options based on selected type
  const filteredPaymentOptions = paymentOptions.filter((option) => {
    if (paymentType === "one_time") {
      return option.type === "one_time";
    } else {
      return option.type !== "one_time" && option.interval_months;
    }
  });

  // Auto-select the highest-value plan: prefer the 3-month bundle if available,
  // otherwise fall back to the first option. Lifts ARPU per the default-plan-3mo task.
  useEffect(() => {
    if (filteredPaymentOptions.length > 0 && !selectedOption) {
      const threeMonth = filteredPaymentOptions.find(
        (option) => option.interval_months === 3
      );
      setSelectedOption(threeMonth ?? filteredPaymentOptions[0]);
    }
  }, [filteredPaymentOptions, selectedOption]);

  // Auto-show coupon input when coupon is applied
  useEffect(() => {
    if (appliedCoupon) {
      setShowCouponInput(true);
    }
  }, [appliedCoupon]);

  const handleOptionSelect = (option: UserAPI.PaymentOption) => {
    setSelectedOption(option);
  };

  const handlePaymentTypeToggle = (type: "subscription" | "one_time") => {
    setPaymentType(type);
    // Clear selected option when switching types so auto-select can pick the first option
    setSelectedOption(null);
    // Track for the payment_toggle_relabel A/B test (primary metric is sub-share at conversion).
    tracking.paymentTypeSelected(type, toggleVariant);
  };
  
  const applyCoupon = useCallback(async (code: string) => {
    if (isNative) {
      toast.error(t('payment.coupon.webOnly'));
      return;
    }
    getPaymentOptions.mutate(
      {
        couponCode: code.trim(),
        bookingId,
      },
      {
        onSuccess: (response) => {
          if (response.success && response.data) {
            const newPaymentOptions = response.data.paymentOptions;
            setPaymentOptions(newPaymentOptions);
            setAppliedCoupon(response.data.couponCode || null);

            // Try to preserve the selected option if it exists in the new options
            setSelectedOption((prev) => {
              if (prev) {
                const matchingOption = newPaymentOptions.find(
                  (opt) => opt.id === prev.id
                );
                return matchingOption || null;
              }
              return null;
            });
            toast.success(t('payment.coupon.applied'));
          } else {
            toast.error(response.error || t('payment.coupon.invalid'));
          }
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error || t('payment.coupon.invalid');
          toast.error(errorMessage);
        },
      }
    );
  }, [getPaymentOptions, bookingId, toast, t, isNative]);

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      toast.error(t('payment.coupon.enterCode'));
      return;
    }
    return applyCoupon(couponCode.trim());
  };

  // Load referrer code from localStorage on mount and pre-fill coupon input
  useEffect(() => {
    // A code chosen upstream is the one they meant; re-pricing with the stored
    // referral here would silently swap it out.
    if (upstreamCoupon) return;
    const storedReferrer = getReferrerCode();
    if (storedReferrer) {
      setCouponCode(storedReferrer);
      setShowCouponInput(true);
      applyCoupon(storedReferrer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveCoupon = () => {
    setPaymentOptions(initialPaymentOptions);
    // Handed options that were already discounted, so the undiscounted ones
    // have to come from the endpoint.
    if (upstreamCoupon) {
      getPaymentOptions.mutate(
        { bookingId },
        {
          onSuccess: (response) => {
            if (response.success && response.data) {
              setPaymentOptions(response.data.paymentOptions);
            }
          },
        }
      );
    }
    setAppliedCoupon(null);
    setCouponCode("");
    setSelectedOption(null);
    toast.success(t('payment.coupon.removed'));
  };

  const handleProceedToPayment = async () => {
    if (!selectedOption) {
      toast.error(t('payment.selectPlan'));
      return;
    }

    // IAP path — no Stripe intent needed at all
    if (purchase.shouldUseNativePurchase() && !useNativeStripe) {
      setIsNativePurchasing(true);
      try {
        const result = await purchase.purchaseProduct(selectedOption.id, bookingId);
        if (result.success) {
          tracking.purchase(selectedOption, bookingId ?? `${selectedOption.id}-${Date.now()}`);
          onSuccessPayment();
        } else if (!result.cancelled) {
          toast.error(result.error || t('payment.purchaseFailed'));
        }
      } catch {
        toast.error(t('payment.purchaseFailed'));
      } finally {
        setIsNativePurchasing(false);
      }
      return;
    }

    try {
      const response = await createPayment.mutateAsync({
        data: {
          bookingId,
          paymentOptionId: selectedOption.id,
          couponId: selectedOption.couponId,
          referral: selectedOption.couponId,
          // Native device identifiers ride into the Stripe intent metadata so the
          // webhook can fire AppsFlyer S2S purchase events. Undefined on web.
          deviceIdentifiers: await tracking.deviceIdentifiersForCheckout(),
        },
      });

      if (response.success && response.data?.type === 'free') {
        clearReferrerCode();
        onSuccessPayment();
        return;
      }
      if (!response.success || !response.data?.clientSecret) {
        toast.error(t('payment.sessionFailed'));
        return;
      }

      if (purchase.shouldUseNativePurchase()) {
        setIsNativePurchasing(true);
        const stripeOptions: NativeStripeOptions = {
          clientSecret: response.data.clientSecret,
          intentType: response.data.type === 'subscription' ? 'setup_intent' : 'payment_intent',
        };
        const result = await purchase.purchaseProduct(selectedOption.id, bookingId, stripeOptions);
        if (result.success) {
          // Server fires the AppsFlyer purchase via S2S for native Stripe.
          tracking.purchase(selectedOption, bookingId ?? `${selectedOption.id}-${Date.now()}`, { viaNativeStripe: true });
          clearReferrerCode();
          onSuccessPayment();
        } else if (!result.cancelled) {
          toast.error(result.error || t('payment.purchaseFailed'));
        }
      } else {
        setPaymentData(response.data);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('payment.sessionFailed'));
    } finally {
      setIsNativePurchasing(false);
    }
  };

  /**
   * Handed a plan the buyer has already chosen somewhere else, this sheet's
   * job is the payment, not the choosing — so it goes straight there.
   *
   * Fires exactly once. Coming back from the card form has to land on the plan
   * list (it is the only way to reach the coupon field from here), and a
   * re-firing effect would bounce them forward again from a screen they just
   * asked to leave.
   *
   * It waits on a stored referral, which is applied on mount and re-prices
   * every option: creating the intent before that lands would charge the
   * undiscounted price and silently drop the referrer's credit.
   */
  const autoProceeded = useRef(false);
  // True while the sheet is on its way to the card form without being asked.
  // Without it the plan list paints for as long as the intent takes to create
  // — a screen the buyer never chose to look at, flashing past.
  const [autoAdvancing, setAutoAdvancing] = useState(
    Boolean(preselectedOptionId),
  );
  const [hadStoredReferral] = useState(
    () => Boolean(getReferrerCode()) && !isNative && !upstreamCoupon,
  );
  const referralSettled =
    !hadStoredReferral || getPaymentOptions.isSuccess || getPaymentOptions.isError;
  useEffect(() => {
    if (!preselectedOptionId || autoProceeded.current) return;
    // The plan asked for may not be on offer here — a single seat has nothing
    // to attach to without a booking, so `subscriptionOnly` mode filters it
    // out. Hand the buyer the list rather than a spinner that never ends.
    if (!paymentOptions.some((option) => option.id === preselectedOptionId)) {
      setAutoAdvancing(false);
      return;
    }
    if (selectedOption?.id !== preselectedOptionId) return;
    if (!referralSettled) return;
    autoProceeded.current = true;
    // Whatever happens next — the card form, a native sheet, an error — the
    // buyer is owed a screen they can act on, so the loader always ends here.
    void handleProceedToPayment().finally(() => setAutoAdvancing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedOptionId, selectedOption, paymentOptions, referralSettled]);

  const handleBackToOptions = () => {
    setPaymentData(null);
    setSelectedOption(null);
    // Asked for the plan list, so stop skipping past it — for good, since
    // `autoProceeded` has already latched.
    setAutoAdvancing(false);
  };

  return (
    <div className="fixed top-safe-padding inset-0 z-50 bg-white dark:bg-neutral-900 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="min-h-full relative"
      >
        {autoAdvancing && !paymentData ? (
          <div className="flex items-center justify-center min-h-[70vh]">
            <Loader2 className="w-8 h-8 animate-spin text-type-900 dark:text-neutral-100" />
          </div>
        ) : !paymentData ? (
          <div className="text-type-900 dark:text-neutral-100">
            {/* Header */}
            <div className="relative px-5 pt-6 pb-4 text-center">
              <div className="absolute top-4 left-4 z-10">
                <button
                  onClick={onCancel}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-type-50 dark:bg-neutral-800 hover:bg-type-100 dark:hover:bg-neutral-700 transition-colors"
                  aria-label="Close"
                >
                  <ChevronLeft className="w-5 h-5 text-type-900 dark:text-neutral-100" />
                </button>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mt-2 mx-12 leading-tight">
                {!bookingId
                  ? t('payment.resumeMembership')
                  : paymentType === "subscription"
                  ? t('payment.subscriptionHeadline')
                  : t('payment.singleTicket')}
              </h1>
              {paymentType === "subscription" && (
                <p className="mt-2 text-base text-type-800/80 dark:text-neutral-300">
                  {t('payment.choosePlan')}
                </p>
              )}
            </div>

            {/* Payment Type Toggle */}
            {showToggle && (
              <div className="flex justify-center px-6 pb-2">
                <div className="relative inline-flex p-1 w-80 h-11 border border-type-200 dark:border-neutral-700 rounded-full bg-type-50 dark:bg-neutral-800">
                  <motion.div
                    className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-white dark:bg-neutral-700 shadow-sm"
                    initial={false}
                    animate={{
                      left: paymentType === "subscription" ? "4px" : "calc(50% + 0px)",
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                  <button
                    onClick={() => handlePaymentTypeToggle("subscription")}
                    className={`w-[50%] relative z-10 text-center rounded-md font-medium text-sm transition-colors ${paymentType === "subscription" ? "text-type-900 dark:text-neutral-100" : "text-type-900/60 dark:text-neutral-400"}`}
                  >
                    {t('payment.membership')}
                  </button>
                  <button
                    onClick={() => handlePaymentTypeToggle("one_time")}
                    className={`w-[50%] relative z-10 text-center rounded-md font-medium text-sm transition-colors ${paymentType === "one_time" ? "text-type-900 dark:text-neutral-100" : "text-type-900/60 dark:text-neutral-400"}`}
                  >
                    {t('payment.singleOuting')}
                  </button>
                </div>
              </div>
            )}

            {/* Plan picker — TimeLeft-style stacked rows */}
            <div className="px-5 pt-2 pb-2 space-y-8">
              {filteredPaymentOptions.length >= 1 && (
                <div className="flex flex-col gap-3 max-w-xl mx-auto">
                  {filteredPaymentOptions.map((option) => {
                    const isSelected = selectedOption?.id === option.id;
                    const is3Month = option.interval_months === 3;
                    const is6Month = option.interval_months === 6;
                    const months = option.interval_months ?? 1;
                    let savingsPercent = 0;
                    if (is3Month) savingsPercent = 22;
                    if (is6Month) savingsPercent = 47;
                    const hasAnchor = !!(
                      option.original_amount && option.original_amount > option.price
                    );
                    const weeklyAmount =
                      paymentType === "subscription" && months > 0
                        ? option.price / (months * 4)
                        : null;
                    const isPopular = is3Month && paymentType === "subscription";
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleOptionSelect(option)}
                        className={`relative flex items-center px-4 py-5 rounded-2xl border-2 w-full text-left transition-all ${
                          isPopular
                            ? `border-amber-400 ${isSelected ? "bg-amber-500/10 dark:bg-amber-500/15 ring-2 ring-amber-400/50" : "bg-amber-500/5 dark:bg-amber-500/8 ring-1 ring-amber-400/25"} shadow-lg shadow-amber-500/20 dark:shadow-amber-500/15 mt-3`
                            : isSelected
                            ? "border-primary-500 bg-primary-500/5 dark:bg-primary-500/10 ring-1 ring-primary-500"
                            : "border-type-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-500"
                        }`}
                      >
                        {isPopular && (
                          <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3.5 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold rounded-full whitespace-nowrap shadow-md uppercase tracking-wider">
                            <Star className="w-3 h-3 fill-white stroke-white" />
                            Most popular
                          </span>
                        )}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0 ${isSelected ? "border-primary-500" : "border-type-300 dark:border-neutral-500"}`}>
                          {isSelected && <div className="w-2.5 h-2.5 bg-primary-500 rounded-full" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-type-900 dark:text-neutral-100">
                            {paymentType === "subscription"
                              ? `${months} ${months === 1 ? t('payment.month') : t('payment.months')}`
                              : t('payment.features.oneDinner')}
                          </p>
                          <p className="text-xs text-type-800/70 dark:text-neutral-400 mt-0.5">
                            {hasAnchor && (
                              <span className="line-through mr-1">
                                {formatPrice(option.original_amount!, option.currency)}
                              </span>
                            )}
                            <span>{formatPrice(option.price, option.currency)}</span>
                            {option.coupon_once && (
                              <span className="ml-1">({t('payment.firstPayment')})</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {savingsPercent > 0 && (
                            <span className="inline-block px-2 py-0.5 mb-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold rounded">
                              {t('payment.save', { percent: savingsPercent })}
                            </span>
                          )}
                          {weeklyAmount !== null ? (
                            <p className="text-base font-semibold text-type-900 dark:text-neutral-100 tabular-nums">
                              {t('payment.perWeek', { price: formatPrice(weeklyAmount, option.currency) })}
                            </p>
                          ) : (
                            <p className="text-base font-semibold text-type-900 dark:text-neutral-100 tabular-nums">
                              {formatPrice(option.price, option.currency)}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Coupon Code Input */}
              <div className="max-w-2xl mx-auto">
                <button
                  onClick={() => setShowCouponInput(!showCouponInput)}
                  className="flex items-center gap-2 text-sm font-medium text-type-900 dark:text-neutral-200 hover:text-type-800 dark:hover:text-neutral-100 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  {t('payment.coupon.haveCode')}
                  <motion.div
                    animate={{ rotate: showCouponInput ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {showCouponInput && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="pt-3">
                        {isNative && !useNativeStripe ? (
                          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                            <p className="text-sm text-amber-800 dark:text-amber-300">
                              {t('payment.coupon.webOnly')}
                            </p>
                          </div>
                        ) : appliedCoupon ? (
                          <div className="flex items-center gap-2 p-3 bg-secondary-800/10 dark:bg-secondary-500/20 border border-secondary-800/30 dark:border-secondary-500/30 rounded-lg">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-secondary-800 dark:text-secondary-300">
                                {t('payment.coupon.codeApplied', { code: appliedCoupon })}
                              </p>
                              <p className="text-xs text-dark-muted dark:text-neutral-400 mt-0.5">
                                {t('payment.coupon.discountReflected')}
                              </p>
                            </div>
                            <button
                              onClick={handleRemoveCoupon}
                              className="text-sm text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 transition-colors px-3 py-1 rounded border border-secondary-600 dark:border-secondary-500 hover:border-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-500/20"
                            >
                              {t('payment.coupon.remove')}
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex gap-2">
                              <input
                                id="coupon-code"
                                type="text"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                placeholder={t('payment.coupon.placeholder')}
                                className="w-full flex-1 px-4 py-2 border border-dark-border dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 text-type-800 dark:text-neutral-100 placeholder-dark-muted dark:placeholder-neutral-500 bg-white dark:bg-neutral-800"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleApplyCoupon();
                                  }
                                }}
                              />
                              <button
                                onClick={handleApplyCoupon}
                                disabled={
                                  !couponCode.trim() || getPaymentOptions.isPending
                                }
                                className="px-6 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {getPaymentOptions.isPending ? (
                                  <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                  t('payment.coupon.apply')
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Terms and Conditions */}
              {paymentType === "subscription" && (
                <div className="text-xs text-gray-500 dark:text-neutral-400 text-center max-w-2xl mx-auto">
                  {t('payment.terms')}
                </div>
              )}

              {/* Start Date Message */}
              {paymentType === "subscription" && startDate && (
                <div className="text-sm text-center max-w-2xl mx-auto p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg">
                  <p className="font-medium text-primary-900 dark:text-primary-300">
                    💳 {bookingId ? t('payment.paymentWillStart') : t('payment.subscriptionWillStart')}{" "}
                    {formatDate(new Date(startDate), DateFormats.full)}
                  </p>
                  <p className="text-xs text-primary-700 dark:text-primary-400 mt-1">
                    {t('payment.activeSubscriptionNote')}
                  </p>
                </div>
              )}

              {/* Subscription pitch sections */}
              {paymentType === "subscription" && (
                <>
                  {/* What you get with Outclose */}
                  <section className="max-w-xl mx-auto">
                    <h2 className="text-xl font-bold mb-4 text-type-900 dark:text-neutral-100">
                      What you get with Outclose
                    </h2>
                    <div className="bg-type-50 dark:bg-neutral-800/60 rounded-2xl p-5 flex flex-col gap-5">
                      {[
                        { color: "bg-orange-500", titleKey: "payment.bullets.newTableTitle", descKey: "payment.bullets.newTableDesc" },
                        { color: "bg-pink-500", titleKey: "payment.bullets.newRestaurantTitle", descKey: "payment.bullets.newRestaurantDesc" },
                        { color: "bg-sky-500", titleKey: "payment.bullets.noPlanningTitle", descKey: "payment.bullets.noPlanningDesc" },
                        { color: "bg-emerald-500", titleKey: "payment.bullets.brunchOrDinnerTitle", descKey: "payment.bullets.brunchOrDinnerDesc" },
                        { color: "bg-purple-500", titleKey: "payment.bullets.cancelAnytimeTitle", descKey: "payment.bullets.cancelAnytimeDesc" },
                      ].map(({ color, titleKey, descKey }) => (
                        <div key={titleKey} className="flex gap-3 items-start">
                          <span className={`w-7 h-7 rounded-full ${color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <Check className="w-4 h-4 text-white" />
                          </span>
                          <div>
                            <p className="font-semibold text-base text-type-900 dark:text-neutral-100">{t(titleKey)}</p>
                            <p className="text-sm text-type-800/80 dark:text-neutral-300 mt-0.5">{t(descKey)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Money-back guarantee (hidden in the guarantee_removal arm) */}
                  {guaranteeDays !== null && (
                    <section className="max-w-xl mx-auto">
                      <div className="rounded-2xl p-7 bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/40">
                        <BadgeCheck
                          className="w-14 h-14 text-emerald-500 mb-4"
                          strokeWidth={2}
                        />
                        <h3 className="text-2xl font-bold text-type-900 dark:text-neutral-100 mb-3 leading-tight">
                          {t('payment.guarantee.title')}
                        </h3>
                        <p className="text-base text-type-800/90 dark:text-neutral-300 leading-relaxed">
                          {t('payment.guarantee.body', { days: guaranteeDays })}
                        </p>
                      </div>
                    </section>
                  )}

                  {/* Hear from our community */}
                  <section className="max-w-xl mx-auto">
                    <h2 className="text-xl font-bold text-type-900 dark:text-neutral-100">
                      Hear from our community
                    </h2>
                    <div className="flex items-center gap-2 mt-1 mb-4 text-sm text-type-800/80 dark:text-neutral-400">
                      <span className="flex items-center gap-0.5">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <Star key={i} className="w-3.5 h-3.5 fill-amber-400 stroke-amber-400" />
                        ))}
                      </span>
                      <span>
                        {t('payment.ratingSummary', {
                          rating: MEMBER_RATING.toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }),
                          members: MEMBER_COUNT.toLocaleString(),
                        })}
                      </span>
                    </div>

                    <div className="flex flex-col gap-3">
                      {testimonialTrio.map((tt) => (
                        <div key={tt.name} className="bg-type-50 dark:bg-neutral-800/60 rounded-2xl p-5">
                          <div className="flex items-center gap-0.5 mb-2">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <Star key={i} className="w-4 h-4 fill-amber-400 stroke-amber-400" />
                            ))}
                          </div>
                          <p className="text-base leading-relaxed text-type-900 dark:text-neutral-100 mb-4">
                            "{tt.quote}"
                          </p>
                          <div className="flex items-center gap-3">
                            <span className={`w-9 h-9 rounded-full ${avatarBgFor(tt.name)} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                              {tt.name.charAt(0)}
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-type-900 dark:text-neutral-100">{tt.name}</p>
                              <p className="text-xs text-type-800/70 dark:text-neutral-400">
                                {tt.age} · {tt.city}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Stats trust strip */}
                    <div className="grid grid-cols-3 gap-2 mt-5 bg-type-50 dark:bg-neutral-800/60 rounded-2xl p-4">
                      <div className="text-center">
                        <p className="text-xl font-bold text-type-900 dark:text-neutral-100">3,500+</p>
                        <p className="text-xs text-type-800/70 dark:text-neutral-400 mt-0.5">outings hosted</p>
                      </div>
                      <div className="text-center border-x border-type-200 dark:border-neutral-700">
                        <p className="text-xl font-bold text-type-900 dark:text-neutral-100">35,000+</p>
                        <p className="text-xs text-type-800/70 dark:text-neutral-400 mt-0.5">members</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-type-900 dark:text-neutral-100">17</p>
                        <p className="text-xs text-type-800/70 dark:text-neutral-400 mt-0.5">cities</p>
                      </div>
                    </div>
                  </section>

                  {/* People often ask */}
                  <section className="max-w-xl mx-auto">
                    <h2 className="text-xl font-bold mb-4 text-type-900 dark:text-neutral-100">
                      {t('payment.faq.heading')}
                    </h2>
                    <div className="flex flex-col gap-2">
                      {[
                        { q: "payment.faq.datingTitle", a: "payment.faq.datingBody" },
                        { q: "payment.faq.matchingTitle", a: "payment.faq.matchingBody" },
                        { q: "payment.faq.meetAgainTitle", a: "payment.faq.meetAgainBody" },
                        { q: "payment.faq.cancelTitle", a: "payment.faq.cancelBody" },
                      ].map(({ q, a }) => (
                        <details key={q} className="bg-type-50 dark:bg-neutral-800/60 rounded-2xl px-4 py-3 group">
                          <summary className="cursor-pointer font-semibold text-sm text-type-900 dark:text-neutral-100 flex justify-between items-center list-none">
                            <span>{t(q)}</span>
                            <ChevronDown className="w-4 h-4 ml-2 transition-transform group-open:rotate-180 flex-shrink-0" />
                          </summary>
                          <p className="text-sm text-type-800/80 dark:text-neutral-300 mt-3 leading-relaxed">{t(a)}</p>
                        </details>
                      ))}
                    </div>
                  </section>

                  {/* Secure checkout */}
                  {!isNative && 
                    <section className="max-w-xl mx-auto text-center pt-2">
                      <div className="flex flex-col items-center">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-type-700 dark:text-neutral-400" aria-hidden="true">
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <h3 className="font-bold text-base mt-2 text-type-900 dark:text-neutral-100">
                          {t('payment.secureCheckout.title')}
                        </h3>
                        <p className="text-xs text-type-800/70 dark:text-neutral-400 mt-1 max-w-sm">
                          {t('payment.secureCheckout.body')}
                        </p>
                      </div>
                    </section>
                  }
                </>
              )}

              {/* Disclaimer */}
              <div className="text-xs text-gray-500 dark:text-neutral-400 text-center max-w-xl mx-auto pb-2">
                {t('payment.disclaimer')}
                {' '}
                <a href="/terms" className="underline hover:text-gray-700 dark:hover:text-neutral-300">
                  {t('profile.legal.terms')}
                </a>
                {isAppleNative && (
                  <>
                    {' | '}
                    <a
                      href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                      target="_blank"
                      className="underline hover:text-gray-700 dark:hover:text-neutral-300"
                    >
                      {t('profile.legal.appleTerms')}
                    </a>
                  </>
                )}
              </div>

              {/* Single-ticket cancellation disclaimer */}
              {paymentType === "one_time" && (
                <div className="text-xs text-gray-500 dark:text-neutral-400 text-center max-w-xl mx-auto pb-2">
                  {t('payment.singleTicketCancellationDisclaimer', { hours: CANCELLATION_REFUND_WINDOW_HOURS })}
                </div>
              )}
            </div>

            {/* Sticky bottom CTA */}
            <div className="sticky bottom-safe-padding bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 border-t border-type-100 dark:border-neutral-800 px-5 pt-3 z-20">
              <div className="max-w-xl mx-auto flex flex-col items-center gap-2 pb-4">
                {selectedOption && (
                  <p className="text-xs text-type-700 dark:text-neutral-400">
                    {paymentType === "subscription"
                      ? selectedOption.interval_months === 1
                        ? t('payment.recurringEveryMonth', {
                            price: formatPrice(selectedOption.price, selectedOption.currency),
                          })
                        : t('payment.recurringEveryMonths', {
                            price: formatPrice(selectedOption.price, selectedOption.currency),
                            months: selectedOption.interval_months,
                          })
                      : t('payment.oneOutingPrice', {
                          price: formatPrice(selectedOption.price, selectedOption.currency),
                        })}
                  </p>
                )}
                <button
                  onClick={handleProceedToPayment}
                  disabled={!selectedOption || createPayment.isPending || isNativePurchasing}
                  className="w-full bg-black dark:bg-primary-600 text-white py-3.5 rounded-full font-semibold text-base hover:bg-gray-800 dark:hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createPayment.isPending || isNativePurchasing ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      {isNativePurchasing ? t('payment.processing') : t('common.buttons.loading')}
                    </span>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Payment Form
          <div className="space-y-6 p-6 max-w-xl mx-auto">
            <div className="flex items-center mr-8">
              <button onClick={handleBackToOptions} className="btn-ghost">
                <ChevronLeft className="w-8 h-8" />
              </button>
              <div className="text-center w-full">
                <h2 className="text-2xl font-bold dark:text-neutral-100">{t('payment.completePayment')}</h2>
                {selectedOption && (
                  <p className="text-dark-muted dark:text-neutral-400">{selectedOption.name}</p>
                )}
              </div>
            </div>

            <PaymentForm
              clientSecret={paymentData.clientSecret}
              onSuccess={() => {
                // Clear referrer from localStorage on successful payment
                clearReferrerCode();
                onSuccessPayment();
              }}
              onCancel={handleBackToOptions}
              paymentOption={selectedOption!}
              startDate={startDate}
            />
          </div>
        )}
      </motion.div>
    </div>
  );
}
